import axios from "axios";
import { SERVER_URL } from "@/lib/settings";

/** The review route talks to the admin endpoints directly — no bearer
 * token, since those endpoints are intentionally left unauthenticated for
 * now (see the design spec). Server-backed, not the local-first
 * IndexedDB collections the capture flow uses: review needs every
 * scanner's scripts, not just this device's. */
const client = axios.create({ baseURL: SERVER_URL });

export interface ReviewPage {
  id: string;
  scriptId: string;
  pageIndex: number;
  sha256: string;
  byteSize: number;
  flags: string[];
  tags: string[];
  notes: string | null;
  rotation: number;
}

export type ReviewStatus = "pending" | "flagged" | "approved";

export interface ReviewScriptListItem {
  id: string;
  label: string | null;
  studentName: string | null;
  subject: string | null;
  scannerName: string;
  createdAt: string;
  pageCount: number;
  flags: string[];
  tags: string[];
  reviewStatus: ReviewStatus;
  flagMessage: string | null;
  flagAckedAt: string | null;
  flagReply: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface ReviewScriptDetail {
  id: string;
  label: string | null;
  createdAt: string;
  rollNumber: string | null;
  studentName: string | null;
  gender: string | null;
  subject: string | null;
  section: string | null;
  groundTruthMarksTotal: number | null;
  groundTruthPerQuestion: unknown;
  customFields: Record<string, unknown> | null;
  flags: string[];
  tags: string[];
  notes: string | null;
  reviewStatus: ReviewStatus;
  flagMessage: string | null;
  flagAckedAt: string | null;
  flagReply: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  pages: ReviewPage[];
}

export interface FieldSuggestions {
  labels: string[];
  subjects: string[];
  sections: string[];
}

export interface ScannerStat {
  name: string;
  pageCount: number;
  scriptCount: number;
}

export interface ScriptMetadataInput {
  label?: string | null;
  rollNumber?: string | null;
  studentName?: string | null;
  gender?: string | null;
  subject?: string | null;
  section?: string | null;
  groundTruthMarksTotal?: number | null;
  groundTruthPerQuestion?: unknown;
  customFields?: Record<string, unknown>;
}

export interface ScriptPatchInput extends ScriptMetadataInput {
  flags?: string[];
  tags?: string[];
  notes?: string | null;
}

export interface PagePatchInput {
  flags?: string[];
  tags?: string[];
  notes?: string | null;
}

/** The checksum rides along as a cache key. A retake replaces the image
 * behind an unchanged page id, so without it the reviewer keeps seeing the
 * photo they asked to have re-shot until they hard-reload. */
export function pageFileUrl(pageId: string, sha256?: string): string {
  const url = `${SERVER_URL}/api/pages/${pageId}/file`;
  return sha256 ? `${url}?v=${sha256}` : url;
}

/** The archive download: every page of a script, in review order, as one
 * PDF. A plain URL rather than a fetch so the browser handles the save. */
export function scriptPdfUrl(scriptId: string): string {
  return `${SERVER_URL}/api/scripts/${scriptId}/pdf`;
}

export async function listReviewScripts(): Promise<ReviewScriptListItem[]> {
  const res = await client.get("/api/scripts");
  return res.data;
}

export async function getReviewScript(id: string): Promise<ReviewScriptDetail> {
  const res = await client.get(`/api/scripts/${id}`);
  return res.data;
}

export async function patchReviewScript(id: string, patch: ScriptPatchInput): Promise<void> {
  await client.patch(`/api/scripts/${id}`, patch);
}

export async function patchReviewPage(id: string, patch: PagePatchInput): Promise<void> {
  await client.patch(`/api/pages/${id}`, patch);
}

export async function patchPageRotation(id: string, rotation: number): Promise<void> {
  await client.patch(`/api/pages/${id}/rotation`, { rotation });
}

export async function deleteReviewPage(id: string): Promise<void> {
  await client.delete(`/api/pages/${id}`);
}

export async function deleteReviewScript(id: string): Promise<void> {
  await client.delete(`/api/scripts/${id}`);
}

export async function reorderReviewPages(scriptId: string, pageIds: string[]): Promise<void> {
  await client.put(`/api/scripts/${scriptId}/order`, { pageIds });
}

export interface SplitScriptResult {
  newScriptId: string;
  sourcePageCount: number;
  newPageCount: number;
}

export async function splitReviewScript(
  sourceId: string,
  pageIds: string[],
): Promise<SplitScriptResult> {
  const res = await client.post(`/api/scripts/${sourceId}/split`, { pageIds });
  return res.data;
}

export async function approveReviewScript(id: string, approvedBy: string): Promise<void> {
  await client.post(`/api/scripts/${id}/approve`, { approvedBy });
}

export async function flagReviewScript(id: string, message: string): Promise<void> {
  await client.post(`/api/scripts/${id}/flag`, { message });
}

export async function getFieldSuggestions(): Promise<FieldSuggestions> {
  const res = await client.get("/api/scripts/suggestions");
  return res.data;
}

/** One capture device's own report of its upload queue. Everything here
 * comes from the phone, because nothing else knows: unsent pages never
 * reach the server. `seenAt` is the server's clock, and a stale one is the
 * point — a device that stopped reporting is a device to go and find. */
export interface DeviceStatus {
  deviceId: string;
  scannerName: string;
  waiting: number;
  blocked: number;
  needsRetake: number;
  maxAttempts: number;
  lastFailReason: string | null;
  pauseReason: string | null;
  oldestWaitingAt: string | null;
  seenAt: string;
  flushRequestedAt: string | null;
}

export async function listDevices(): Promise<DeviceStatus[]> {
  const res = await client.get("/api/devices");
  return res.data.devices;
}

/** Asks a device to push its whole backlog. Takes effect on that device's
 * next heartbeat (within ~20s) — and only if its app is open, which is the
 * one condition this cannot work around from here. */
export async function requestDeviceFlush(deviceId: string): Promise<void> {
  await client.post(`/api/devices/${deviceId}/flush`);
}

export async function getScannerStats(from: string, to: string): Promise<ScannerStat[]> {
  const res = await client.get("/api/stats/scanners", { params: { from, to } });
  return res.data.scanners;
}

export async function getApproverStats(from: string, to: string): Promise<ScannerStat[]> {
  const res = await client.get("/api/stats/approvers", { params: { from, to } });
  return res.data.approvers;
}
